import { useMemo } from 'react'
import type { HierarchyLevel } from '@/api/productHierarchy'
import { useHierarchyLevel } from '@/hooks/useHierarchy'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'

interface HierarchyValueSelectProps {
  level: HierarchyLevel
  value: string | string[]
  onChange: (value: string | string[]) => void
  multiple: boolean
  parentId?: string | null
}

export function HierarchyValueSelect({
  level,
  value,
  onChange,
  multiple,
  parentId,
}: HierarchyValueSelectProps) {
  const { data: nodes, isLoading } = useHierarchyLevel(level, parentId)

  const options = useMemo(
    () =>
      (nodes ?? []).map(n => ({
        value: n.id,
        label: `${n.code} - ${n.name}`,
      })),
    [nodes]
  )

  if (multiple) {
    const arrValue = Array.isArray(value) ? value : value ? [value] : []
    return (
      <MultiSelect
        options={options}
        value={arrValue}
        onValueChange={onChange}
        placeholder="Select values..."
        isLoading={isLoading}
        className="flex-1"
      />
    )
  }

  const strValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '')

  return (
    <Select value={strValue} onValueChange={v => onChange(v)}>
      <SelectTrigger className="flex-1">
        <SelectValue
          placeholder={isLoading ? 'Loading...' : 'Select value...'}
        />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
